export type AppRole =
  | "super_admin"
  // Whole-org admin: sees and manages every workspace in the org. Also
  // satisfies every "admin" check (has_role(x,'admin') is true in the DB).
  | "org_admin"
  // Manager: 'admin' role scoped to the workspaces in workspace_managers.
  | "admin"
  // Dead role — still present in the DB enum for legacy rows, but it grants
  // nothing in the console. Kept in the union so user_roles reads still type.
  | "team_lead"
  | "staff"
  | "production_staff"
  // Derived client-side from a client_users row (NEVER stored in user_roles).
  // A client-portal login carries only this role and reaches only /client.
  | "client";

export type AppMode = "staff" | "crew" | "admin";

/** Roles that open the admin console. */
export const ADMIN_ROLES: AppRole[] = ["super_admin", "org_admin", "admin"];

/** Roles that may manage the whole organization (workspaces, org admins). */
export const ORG_ADMIN_ROLES: AppRole[] = ["super_admin", "org_admin"];

/** Roles that may use the staff app (/app). Excludes client. */
export const STAFF_APP_ROLES: AppRole[] = [
  "super_admin",
  "org_admin",
  "admin",
  "staff",
  "production_staff",
];

export function isAdminRole(role: AppRole | null | undefined): boolean {
  return role !== null && role !== undefined && ADMIN_ROLES.includes(role);
}

/** True when the role set carries org-wide admin power (org admin or super admin). */
export function isOrgAdminRole(roles: AppRole[] | null | undefined): boolean {
  return Boolean(roles?.some((r) => ORG_ADMIN_ROLES.includes(r)));
}

/**
 * Default landing path for an authenticated user: client-portal logins go to
 * /client, admins (super/org admin/manager) to the admin console, everyone
 * else to the staff app. Centralized so sign-in, set-password, and "Open app"
 * CTAs stay in sync.
 */
export function homePathForRoles(roles: AppRole[] | null | undefined): string {
  if (roles?.includes("client")) return "/client";
  if (roles?.some((r) => ADMIN_ROLES.includes(r))) return "/admin";
  return "/app";
}
